import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import {
  getSheet_lookupsNonMaturity,
  getSheet_NonMaturityCPTables as getSheet_cpTablesNonMaturity
} from '@components/export-alm/export-alm/export-alm-spread-non-maturity';
import {
  getSheet_Forecasts,
  getSheet_lookupsVintage,
  getSheet_VintageCPTables as getSheet_cpTablesVintage
} from '@components/export-alm/export-alm/export-alm-spread-vintage';
import * as GC from '@grapecity/spread-sheets';
import { IDecayFactorRunOff } from '@models/export-alm/decay-factor-runoff';
import { ExportAlmMethod } from '@models/export-alm/export-alm-method';
import { IExportAlmRequest } from '@models/export-alm/export-alm-request';
import { IExportAlmNonMaturityResponse, IExportAlmVintageResponse } from '@models/export-alm/export-alm-response';
import { IRunOffsDetail } from '@models/export-alm/run-offs-detail';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';
import { catchError, debounceTime, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ExportAlmService {
  private lookupsSheetNonMaturity: GC.Spread.Sheets.Worksheet = null;
  private cpTableSheetNonMaturity: GC.Spread.Sheets.Worksheet = null;

  private lookupsSheetVintage: GC.Spread.Sheets.Worksheet = null;
  private cpTableSheetVintage: GC.Spread.Sheets.Worksheet = null;
  private forecastsSheetVintage: GC.Spread.Sheets.Worksheet = null;

  private exportNonMaturityAPIEndpoint: string;
  private exportVintageAPIEndpoint: string;
  private exportCsvEndpoint: string;
  private exportProductMapping: string;
  private lastRequest: IExportAlmRequest = null;
  private runoffsDetailsEndpoint: string;
  private readonly _exportAlmMenuOptionDisabled$ = new BehaviorSubject<boolean>(true);
  private readonly appConfigSubscription: Subscription;

  /**
   * Holds total number of items in CP_Data database table
   */
  public totalNumberOfRows: number;

  /**
   * Holds number of displayed items in spreadsheet
   * (max. 600k defined in sproc 'prGetCPTableVintageMethod')
   */
  public displayedNumberOfRows: number;

  public get exportAlmMenuOptionDisabled$() {
    return this._exportAlmMenuOptionDisabled$.asObservable();
  }

  public setExportAlmMenuOption(value: boolean) {
    this._exportAlmMenuOptionDisabled$.next(value);
  }

  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfigProvider) {
    this.appConfigSubscription = this.appConfig.environment.subscribe(env => {
      this.exportNonMaturityAPIEndpoint = `${env.API.EXPORT_ALM}/api/export/non-maturity`;
      this.exportVintageAPIEndpoint = `${env.API.EXPORT_ALM}/api/export/vintage`;
      this.exportCsvEndpoint = `${env.API.EXPORT_ALM}/api/export/cp-data-csv`;
      this.runoffsDetailsEndpoint = `${env.API.EXPORT_ALM}/api/export/runoffs-details`;
      this.exportProductMapping = `${env.API.EXPORT_ALM}/api/export/product-mapping`;
    });
    this.appConfigSubscription.unsubscribe();
  }

  public exportNonMaturity(request: IExportAlmRequest): Observable<IExportAlmNonMaturityResponse> {
    this.resetState();

    return this.http.post<IExportAlmNonMaturityResponse>(this.exportNonMaturityAPIEndpoint, request).pipe(
      map(response => {
        const { RunOffs, Segments } = response;

        // Restoring ordering of Lookups to be the same as in request.ProductIds
        response.Lookups = request.ProductIds.map(productId => response.Lookups.find(l => l.ProductId === productId)).filter(Boolean);
        const { Lookups } = response;

        this.lastRequest = request;

        Lookups.forEach(lookup => {
          // Attaching RunOffs to products.
          lookup.RunOff = RunOffs.find(ro => ro.ProductId === lookup.ProductId) || null;

          // Attaching Segments to products.
          lookup.Segments = Segments.filter(s => s.ProductId === lookup.ProductId);
        });

        // Removing Period 0 from lookups 🛠 9165
        Lookups.forEach(lookup => {
          if (lookup && lookup.RunOff && lookup.RunOff.VintageRunOffScheduleItems && lookup.RunOff.VintageRunOffScheduleItems.length > 0) {
            lookup.RunOff.VintageRunOffScheduleItems.shift();
          }
        });

        this.lookupsSheetNonMaturity = getSheet_lookupsNonMaturity(Lookups);
        this.cpTableSheetNonMaturity = getSheet_cpTablesNonMaturity(Lookups, Segments);

        return response;
      }),
      catchError(err => {
        console.warn(`❌ exportNonMaturity`, { err });
        throw err;
      })
    );
  }

  public exportVintage(request: IExportAlmRequest): Observable<IExportAlmVintageResponse> {
    this.resetState();

    return forkJoin([
      this.http.post<IExportAlmVintageResponse>(this.exportVintageAPIEndpoint, request).pipe(
        map(res => {
          res.Segments = res.Segments.map(segment => ({
            SecID: segment.s,
            AccountNum: segment.a,
            DepProductMn: segment.d,
            VintageType: segment.v.trim(),
            IssueDate: segment.i,
            OriginalBook: segment.o,
            AvgBook: segment.k,
            EOPBook: segment.e,
            CurrCoupon: segment.c,
            FTPBeta: segment.b,
            CustomFields: segment.f
          }));
          return res;
        })
      )
    ]).pipe(
      map(res => {
        const [almResponse] = res;

        // @ts-ignore
        almResponse.RunOffs = JSON.parse(almResponse.RunOffs || '[]') as IDecayFactorRunOff[];

        const { RunOffs, Segments, NumberOfRows } = almResponse;
        const numberOfSegments = Segments.length;

        // Restoring ordering of Lookups and Forecasts to be the same as in request.ProductIds
        almResponse.Lookups = request.ProductIds.map(productId => almResponse.Lookups.find(l => l.ProductId === productId)).filter(Boolean);
        almResponse.Forecasts = request.ProductIds.map(productId => almResponse.Forecasts.find(f => f.ProductId === productId)).filter(
          Boolean
        );
        const { Lookups, Forecasts } = almResponse;

        this.lastRequest = request;
        this.totalNumberOfRows = NumberOfRows;
        this.displayedNumberOfRows = numberOfSegments;

        Lookups.forEach(lookup => {
          // Attaching RunOffs to products.
          lookup.RunOff = RunOffs.find(ro => ro.ProductId === lookup.ProductId) || null;

          // Attaching Segments to products.
          lookup.Segments = Segments ? Segments.filter(s => s.DepProductMn === lookup.ProductMnemonic) : [];
        });

        this.lookupsSheetVintage = getSheet_lookupsVintage(Lookups);

        const userFieldColumns: string[] = Segments.reduce(
          (acc, s) => Array.from(new Set([...acc, ...Object.keys(s.CustomFields)])),
          []
        ).sort();

        this.cpTableSheetVintage = getSheet_cpTablesVintage(Lookups, userFieldColumns, numberOfSegments);
        if (ExportAlmMethod.Vintage === request.ExportMethod) {
          this.forecastsSheetVintage = getSheet_Forecasts(Forecasts);
        }

        return almResponse;
      })
    );
  }

  public getExportResults() {
    return {
      request: this.lastRequest,

      lookupsSheetNonMaturity: this.lookupsSheetNonMaturity,
      cpTableSheetNonMaturity: this.cpTableSheetNonMaturity,

      lookupsSheetVintage: this.lookupsSheetVintage,
      cpTableSheetVintage: this.cpTableSheetVintage,
      forecastsSheetVintage: this.forecastsSheetVintage
    };
  }

  public getRunOffsDetails(): Observable<IRunOffsDetail[]> {
    return this.http
      .get<IRunOffsDetail[]>(this.runoffsDetailsEndpoint)
      .pipe(map(runoffs => runoffs.sort((a, b) => b.RunOffType - a.RunOffType).sort((a, b) => a.ProductId - b.ProductId)));
  }

  public getExpectedRows(request: IExportAlmRequest) {
    return this.http.post<{ rows: number }>(`${this.exportVintageAPIEndpoint}/rows`, request).pipe(debounceTime(1000));
  }

  public getExportCsvZipBlob() {
    return this.http
      .post(this.exportCsvEndpoint, this.lastRequest, {
        responseType: 'arraybuffer',
        observe: 'response'
      })
      .pipe(map(response => new Blob([response.body], { type: 'application/octet-stream' })));
  }

  public saveProductMapping() {
    if (!this.lastRequest) {
      return null;
    }
    return this.http.post<IExportAlmRequest>(this.exportProductMapping, this.lastRequest).pipe(debounceTime(10));
  }

  public resetState() {
    this.lastRequest = null;

    this.lookupsSheetNonMaturity = null;
    this.cpTableSheetNonMaturity = null;

    this.lookupsSheetVintage = null;
    this.cpTableSheetVintage = null;
    this.forecastsSheetVintage = null;
  }
}
